/**
 * DutyTimePage
 * Admin duty time compliance dashboard.
 * Shows all pilots' duty status sorted by most constrained.
 */

import { useState } from 'react';
import { useAllPilotsStatus, useDutyHistory } from '@/hooks/useDutyTime';
import DutyStatusCard from '@/components/DutyStatusCard';
import DutyStatusBadge from '@/components/DutyStatusBadge';
import type { PilotDutyStatus } from '@/services/dutyTimeService';
import {
  ClockIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

type FilterStatus = 'all' | 'red' | 'amber' | 'green';

export default function DutyTimePage() {
  const { data: statuses, isLoading, isError } = useAllPilotsStatus();
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [expandedPilotId, setExpandedPilotId] = useState<number | null>(null);

  const filteredStatuses = statuses?.filter(s =>
    filter === 'all' ? true : s.overallStatus === filter
  ) ?? [];

  const counts = {
    all: statuses?.length ?? 0,
    red: statuses?.filter(s => s.overallStatus === 'red').length ?? 0,
    amber: statuses?.filter(s => s.overallStatus === 'amber').length ?? 0,
    green: statuses?.filter(s => s.overallStatus === 'green').length ?? 0,
  };

  const totalAlerts = statuses?.reduce((sum, s) => sum + s.alerts.length, 0) ?? 0;

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <ClockIcon className="w-8 h-8 text-primary" />
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground">
                Duty Time Compliance
              </h1>
              <p className="text-muted-foreground mt-1">
                CARs 700.14-700.20 flight &amp; duty time monitoring
              </p>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          <SummaryCard label="Total Pilots" value={counts.all} color="text-foreground" />
          <SummaryCard label="Alerts" value={totalAlerts} color={totalAlerts > 0 ? 'text-danger' : 'text-success'} />
          <SummaryCard label="At Risk" value={counts.red + counts.amber} color={counts.red > 0 ? 'text-danger' : 'text-muted-foreground'} />
          <SummaryCard label="Clear" value={counts.green} color="text-success" />
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6 flex-wrap">
          {(['all', 'red', 'amber', 'green'] as const).map(f => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                filter === f
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card border border-border text-muted-foreground hover:bg-muted'
              }`}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              <span className="ml-1.5 opacity-70">({counts[f]})</span>
            </button>
          ))}
        </div>

        {/* Pilot List */}
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-card rounded-lg border border-border p-6 animate-pulse">
                <div className="h-5 w-40 bg-muted rounded mb-3" />
                <div className="h-3 w-64 bg-muted rounded" />
              </div>
            ))}
          </div>
        ) : isError ? (
          <div className="bg-card rounded-lg border border-danger/30 p-8 text-center">
            <ExclamationTriangleIcon className="w-10 h-10 mx-auto text-danger mb-2" />
            <p className="text-foreground font-medium">Failed to load duty time data</p>
            <p className="text-muted-foreground text-sm mt-1">Check your connection and refresh</p>
          </div>
        ) : filteredStatuses.length === 0 ? (
          <div className="bg-card rounded-lg border border-border p-8 text-center">
            <ClockIcon className="w-10 h-10 mx-auto text-muted-foreground mb-2" />
            <p className="text-foreground font-medium">No pilots match this filter</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredStatuses.map(status => (
              <PilotRow
                key={status.pilotId}
                status={status}
                isExpanded={expandedPilotId === status.pilotId}
                onToggle={() => setExpandedPilotId(
                  expandedPilotId === status.pilotId ? null : status.pilotId
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────────────────────

function SummaryCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="bg-card rounded-lg border border-border p-4">
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </div>
  );
}

function PilotRow({
  status,
  isExpanded,
  onToggle,
}: {
  status: PilotDutyStatus;
  isExpanded: boolean;
  onToggle: () => void;
}) {
  // Find the most critical window
  const maxPercent = Math.max(
    status.windows.hours7day.percent,
    status.windows.hours30day.percent,
    status.windows.hours90day.percent,
    status.windows.hours365day.percent,
  );

  return (
    <div className="bg-card rounded-lg border border-border overflow-hidden">
      {/* Clickable summary row */}
      <button
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-4">
          <DutyStatusBadge
            overallStatus={status.overallStatus}
            alertCount={status.alerts.length}
            size="md"
          />
          <div className="text-left">
            <p className="font-medium text-foreground">{status.pilotName}</p>
            <p className="text-xs text-muted-foreground">
              {status.homeBase || 'No home base'}
              {status.currentFDP.active && ' \u2022 On duty'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-4">
          {/* Mini bar for the highest window usage */}
          <div className="hidden sm:flex items-center gap-2">
            <span className="text-xs text-muted-foreground w-12 text-right">
              {maxPercent.toFixed(0)}%
            </span>
            <div className="w-24 bg-muted rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  maxPercent >= 100 ? 'bg-danger' : maxPercent >= 80 ? 'bg-warning' : 'bg-primary'
                }`}
                style={{ width: `${Math.min(maxPercent, 100)}%` }}
              />
            </div>
          </div>

          {isExpanded ? (
            <ChevronUpIcon className="w-5 h-5 text-muted-foreground" />
          ) : (
            <ChevronDownIcon className="w-5 h-5 text-muted-foreground" />
          )}
        </div>
      </button>

      {/* Expanded detail */}
      {isExpanded && (
        <div className="border-t border-border">
          <div className="p-4">
            <DutyStatusCard status={status} />
          </div>
          <PilotHistory pilotId={status.pilotId} />
        </div>
      )}
    </div>
  );
}

function PilotHistory({ pilotId }: { pilotId: number }) {
  const { data, isLoading } = useDutyHistory(pilotId);

  if (isLoading) {
    return (
      <div className="px-4 pb-4">
        <div className="h-4 w-32 bg-muted rounded animate-pulse" />
      </div>
    );
  }

  if (!data || data.auditLog.length === 0) {
    return (
      <div className="px-4 pb-4 text-sm text-muted-foreground">
        No duty time history recorded yet.
      </div>
    );
  }

  return (
    <div className="px-4 pb-4">
      <h4 className="text-sm font-medium text-foreground mb-2">Recent Activity</h4>
      <div className="space-y-1 max-h-48 overflow-y-auto">
        {data.auditLog.slice(0, 10).map(entry => (
          <div key={entry.id} className="flex items-start gap-2 text-xs">
            <span className="text-muted-foreground whitespace-nowrap min-w-[120px]">
              {new Date(entry.createdAt).toLocaleString(undefined, {
                month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
              })}
            </span>
            <span className={`font-medium ${
              entry.eventType.includes('exceeded') ? 'text-danger'
              : entry.eventType.includes('warning') ? 'text-warning'
              : 'text-foreground'
            }`}>
              {entry.message}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}
